/** Чистые операции тулбара markdown-редактора (тестируются без DOM). */

export type TextSelection = {
  readonly value: string;
  readonly start: number;
  readonly end: number;
};

export type TextEdit = {
  readonly value: string;
  /** Позиция каретки после правки. */
  readonly caret: number;
};

/** Оборачивает выделение парой маркеров: `**жирный**`, `_курсив_`, `` `код` ``. */
export function wrapSelection(selection: TextSelection, marker: string): TextEdit {
  const { value, start, end } = clampSelection(selection);
  const before = value.slice(0, start);
  const selected = value.slice(start, end);
  const after = value.slice(end);
  const text = selected.length > 0 ? selected : 'текст';
  const wrapped = `${marker}${text}${marker}`;
  return {
    value: `${before}${wrapped}${after}`,
    caret: start + marker.length + text.length,
  };
}

/** Вставляет список: каждая строка выделения становится пунктом. */
export function insertList(selection: TextSelection, ordered = false): TextEdit {
  const { value, start, end } = clampSelection(selection);
  const lineStart = value.lastIndexOf('\n', Math.max(0, start - 1)) + 1;
  const lineEndCandidate = value.indexOf('\n', end);
  const lineEnd = lineEndCandidate === -1 ? value.length : lineEndCandidate;
  const block = value.slice(lineStart, lineEnd);
  const lines = block.length > 0 ? block.split('\n') : [''];
  const prefixed = lines
    .map((line, index) => (ordered ? `${index + 1}. ${line}` : `- ${line}`))
    .join('\n');
  return {
    value: `${value.slice(0, lineStart)}${prefixed}${value.slice(lineEnd)}`,
    caret: lineStart + prefixed.length,
  };
}

/** Вставляет ссылку `[текст](url)`, сохраняя выделение как текст ссылки. */
export function insertLink(selection: TextSelection): TextEdit {
  const { value, start, end } = clampSelection(selection);
  const selected = value.slice(start, end);
  const text = selected.length > 0 ? selected : 'текст';
  const inserted = `[${text}](url)`;
  return {
    value: `${value.slice(0, start)}${inserted}${value.slice(end)}`,
    caret: start + inserted.length - 1,
  };
}

/** Вставляет блок кода на три строки. */
export function insertCodeBlock(selection: TextSelection): TextEdit {
  const { value, start, end } = clampSelection(selection);
  const selected = value.slice(start, end);
  const inserted = `\`\`\`\n${selected}\n\`\`\``;
  return {
    value: `${value.slice(0, start)}${inserted}${value.slice(end)}`,
    caret: start + inserted.length,
  };
}

/** `Cmd/Ctrl+Enter` — отправка (docs/06 §3.2). */
export function isSubmitHotkey(event: { readonly key: string; readonly metaKey: boolean; readonly ctrlKey: boolean }): boolean {
  return event.key === 'Enter' && (event.metaKey || event.ctrlKey);
}

/** Подсчёт упоминаний `@login` для предпросмотра. */
export function extractMentions(value: string): readonly string[] {
  const matches = value.matchAll(/(?:^|\s)@([a-z0-9][a-z0-9._-]{1,31})/gi);
  const logins = new Set<string>();
  for (const match of matches) {
    const login = match[1];
    if (login) {
      logins.add(login.toLowerCase());
    }
  }
  return [...logins];
}

function clampSelection(selection: TextSelection): TextSelection {
  const max = selection.value.length;
  const start = Math.max(0, Math.min(selection.start, max));
  const end = Math.max(start, Math.min(selection.end, max));
  return { value: selection.value, start, end };
}
